import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CreditCard, AlertCircle, Receipt, Smartphone, DollarSign, QrCode, SplitSquareHorizontal, ExternalLink, Wallet } from "lucide-react";
import usePaymentMethods from "../hooks/usePaymentMethods";
import { PaymentService, PaymentMethod, PartialCodCharge } from "../services/paymentService";
import useToastStore from "../store/useToastStore";

// ─── Shared small components ────────────────────────────────────────────────

const Modal: React.FC<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
}> = ({ isOpen, title, message, onConfirm, onCancel }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
                <h2 className="text-lg font-semibold mb-4">{title}</h2>
                <p className="mb-6 text-gray-600">{message}</p>
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
                    <button onClick={onCancel} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 w-full sm:w-auto">
                        Cancel
                    </button>
                    <button onClick={onConfirm} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full sm:w-auto">
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
};

const ToggleButton: React.FC<{
    enabled: boolean;
    disabled?: boolean;
    onToggle: () => void;
}> = ({ enabled, disabled, onToggle }) => (
    <div className="flex items-center gap-2">
        <span className={`text-sm font-medium ${enabled ? "text-green-600" : "text-gray-500"}`}>
            {enabled ? "Active" : "Inactive"}
        </span>
        <button
            onClick={onToggle}
            disabled={disabled}
            className={`relative w-14 h-7 rounded-full transition duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${enabled ? "bg-blue-600" : "bg-gray-200"}`}
        >
            <div className={`absolute w-5 h-5 bg-white rounded-full shadow-md top-1 transition-all duration-200 ${enabled ? "right-1" : "left-1"}`} />
        </button>
    </div>
);

const FormActions: React.FC<{
    isSaving: boolean;
    onSave: () => void;
    onCancel: () => void;
}> = ({ isSaving, onSave, onCancel }) => (
    <div className="flex gap-3">
        <button onClick={onSave} disabled={isSaving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200">
            {isSaving ? "Saving..." : "Save"}
        </button>
        <button onClick={onCancel} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200">
            Cancel
        </button>
    </div>
);

const ConfigDisplay: React.FC<{
    onEdit: () => void;
    children: React.ReactNode;
}> = ({ onEdit, children }) => (
    <div className="border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-blue-800">Configuration</h3>
            <button onClick={onEdit} className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm font-medium">
                Edit
            </button>
        </div>
        <div className="space-y-1">{children}</div>
    </div>
);

const ChargesEditor: React.FC<{
    charges: PartialCodCharge[];
    onChange: (charges: PartialCodCharge[]) => void;
    labelPlaceholder?: string;
    valuePlaceholder?: string;
}> = ({ charges, onChange, labelPlaceholder = "e.g. COD Fee", valuePlaceholder = "e.g. 50" }) => {
    const add = () => onChange([...charges, { label: "", value: 0, type: "fixed" }]);
    const remove = (i: number) => onChange(charges.filter((_, idx) => idx !== i));
    const update = (i: number, key: keyof PartialCodCharge, value: string | number) =>
        onChange(charges.map((c, idx) => idx === i ? { ...c, [key]: value } : c));

    return (
        <div className="space-y-2">
            {charges.map((charge, i) => (
                <div key={i} className="flex gap-2 items-center">
                    <input
                        type="text"
                        value={charge.label}
                        onChange={e => update(i, "label", e.target.value)}
                        placeholder={labelPlaceholder}
                        className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                    <input
                        type="number"
                        value={charge.value || ""}
                        onChange={e => update(i, "value", Number(e.target.value))}
                        placeholder={valuePlaceholder}
                        className="w-24 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                    <select
                        value={charge.type}
                        onChange={e => update(i, "type", e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                    >
                        <option value="fixed">Fixed (₹)</option>
                        <option value="percentage">Percentage (%)</option>
                    </select>
                    <button onClick={() => remove(i)} className="p-2 text-red-500 hover:text-red-700 text-lg leading-none">×</button>
                </div>
            ))}
            <button onClick={add} className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                + Add Charge
            </button>
        </div>
    );
};

const ChargesList: React.FC<{
    charges: PartialCodCharge[];
    title: string;
}> = ({ charges, title }) => {
    if (!charges.length) return null;
    return (
        <div className="mt-2">
            <p className="text-sm font-medium text-blue-700">{title}:</p>
            {charges.map((c, i) => (
                <p key={i} className="text-sm text-blue-700 ml-2">
                    • {c.label}: {c.type === "percentage" ? `${c.value}%` : `₹${c.value}`}
                </p>
            ))}
        </div>
    );
};

// Card shell shared by PartialCodCard and RazorpayCard
const ConfigCard: React.FC<{
    icon: React.ReactNode;
    iconBgColor: string;
    iconColor: string;
    title: string;
    description: string;
    isConfigured: boolean;
    isEditing: boolean;
    onEdit: () => void;
    method: PaymentMethod;
    onToggle: () => void;
    isSaving: boolean;
    children: React.ReactNode;
}> = ({ icon, iconBgColor, iconColor, title, description, isConfigured, isEditing, onEdit, method, onToggle, isSaving, children }) => (
    <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
        <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-full ${iconBgColor} flex-shrink-0 flex items-center justify-center`}>
                        <div className={iconColor}>{icon}</div>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900">{title}</h3>
                </div>
                {isConfigured ? (
                    <ToggleButton enabled={method.enabled} disabled={isSaving} onToggle={onToggle} />
                ) : !isEditing && (
                    <button onClick={onEdit} className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm font-medium">
                        Add
                    </button>
                )}
            </div>
            <p className="text-sm text-gray-600 mb-4">{description}</p>
            {children}
        </div>
    </div>
);

// ─── Data-driven payment methods (COD, MANUAL_UPI, PHONEPE) ─────────────────

interface ConfigField {
    key: string;
    label: string;
    type: string;
    placeholder: string;
    example?: string;
    selectOptions?: { value: string; label: string }[];
}

interface PaymentMethodConfig {
    type: string;
    name: string;
    description: string;
    icon: React.ReactNode;
    iconBgColor: string;
    iconColor: string;
    requiresConfig: boolean;
    configFields?: ConfigField[];
    infoMessage?: {
        title: string;
        message: string;
        bgColor: string;
        borderColor: string;
        textColor: string;
        iconColor: string;
    };
}

const PAYMENT_METHOD_CONFIGS: Record<string, PaymentMethodConfig> = {
    PHONEPE: {
        type: "PHONEPE",
        name: "PhonePe",
        description: "Accept payments through PhonePe UPI, cards, and other digital payment methods",
        icon: <Smartphone className="h-5 w-5" />,
        iconBgColor: "bg-purple-100",
        iconColor: "text-purple-600",
        requiresConfig: true,
        configFields: [
            { key: "clientId", label: "Client ID", type: "text", placeholder: "Enter PhonePe Client ID" },
            { key: "clientSecret", label: "Client Secret", type: "password", placeholder: "Enter PhonePe Client Secret" },
        ],
    },
    COD: {
        type: "COD",
        name: "COD (Cash on Delivery)",
        description: "Allow customers to pay with cash when their order is delivered",
        icon: <DollarSign className="h-5 w-5" />,
        iconBgColor: "bg-orange-100",
        iconColor: "text-orange-600",
        requiresConfig: true,
        configFields: [
            { key: "extraCharges", label: "Extra Charge (₹)", type: "number", placeholder: "Enter extra charge amount (e.g., 50)", example: "Example: 50 (will add ₹50 extra charge for COD orders)" },
        ],
        infoMessage: {
            title: "Important Note",
            message: "COD orders require manual verification and cash collection upon delivery. Make sure your delivery team is prepared to handle cash transactions. Extra charge will be added to the total order amount.",
            bgColor: "bg-orange-50",
            borderColor: "border-orange-200",
            textColor: "text-orange-700",
            iconColor: "text-orange-600",
        },
    },
    MANUAL_UPI: {
        type: "MANUAL_UPI",
        name: "Manual UPI Payment",
        description: "Display your UPI ID for customers to make manual payments",
        icon: <QrCode className="h-5 w-5" />,
        iconBgColor: "bg-blue-100",
        iconColor: "text-blue-600",
        requiresConfig: true,
        configFields: [
            { key: "upiId", label: "UPI ID", type: "text", placeholder: "Enter your UPI ID (e.g., yourname@upi)", example: "Example: 7518388162@ybl, yourname@okicici, etc." },
        ],
        infoMessage: {
            title: "How it works",
            message: "Customers will see your UPI ID during checkout and can make payments directly to your UPI ID. You'll need to manually verify payments and update order status.",
            bgColor: "bg-blue-50",
            borderColor: "border-blue-200",
            textColor: "text-blue-700",
            iconColor: "text-blue-600",
        },
    },
};

const PaymentMethodCard: React.FC<{ method: PaymentMethod; onToggle: () => void }> = ({ method, onToggle }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [configValues, setConfigValues] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);
    const { refetch } = usePaymentMethods();
    const toast = useToastStore();

    const config = PAYMENT_METHOD_CONFIGS[method.type];

    React.useEffect(() => {
        if (method.config && config?.configFields) {
            const values: Record<string, string> = {};
            config.configFields.forEach(field => {
                const value = method.config?.[field.key as keyof typeof method.config];
                values[field.key] = typeof value === "string" ? value : "";
            });
            setConfigValues(values);
        }
    }, [method.config, config?.configFields]);

    if (!config) return null;

    const isConfigured = config.requiresConfig
        ? config.configFields?.every(field => {
            const value = method.config?.[field.key as keyof typeof method.config];
            if (field.key === "extraCharges" || field.key === "amount") return value !== undefined && value !== null;
            return typeof value === "string" && value.trim() !== "";
        })
        : true;

    const handleConfigChange = (key: string, value: string) =>
        setConfigValues(prev => ({ ...prev, [key]: value }));

    const handleSave = async () => {
        if (config.requiresConfig && config.configFields) {
            const missing = config.configFields.filter(f => !configValues[f.key]?.trim());
            if (missing.length > 0) {
                toast.showToast(`Please fill in all required fields: ${missing.map(f => f.label).join(", ")}`, "error");
                return;
            }
        }
        setIsSaving(true);
        try {
            switch (method.type) {
                case "PHONEPE":
                    await PaymentService.updatePhonePeConfig(configValues.clientId || "", configValues.clientSecret || "", []);
                    break;
                case "COD":
                    await PaymentService.updateCodConfig(Number(configValues.extraCharges) || 0);
                    break;
                case "MANUAL_UPI":
                    await PaymentService.updateManualUpiConfig(configValues.upiId || "");
                    break;
                default:
                    throw new Error(`Unsupported payment method type: ${method.type}`);
            }
            await refetch();
            setIsEditing(false);
            toast.showToast(`${config.name} configuration updated successfully`, "success");
        } catch (error) {
            console.error(`Failed to update ${config.name} config:`, error);
            toast.showToast(`Failed to update ${config.name} configuration. Please try again.`, "error");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
            <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-full ${config.iconBgColor} flex-shrink-0 flex items-center justify-center`}>
                            <div className={config.iconColor}>{config.icon}</div>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900">{config.name}</h3>
                    </div>
                    {isConfigured ? (
                        <ToggleButton enabled={method.enabled} disabled={isSaving} onToggle={onToggle} />
                    ) : !isEditing && (
                        <button onClick={() => setIsEditing(true)} className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm font-medium">
                            Add
                        </button>
                    )}
                </div>

                <p className="text-sm text-gray-600 mb-4">{config.description}</p>

                {isEditing && config.requiresConfig ? (
                    <div className="space-y-4">
                        {config.configFields?.map(field => (
                            <div key={field.key}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                                {field.type === "select" ? (
                                    <select
                                        value={configValues[field.key] || ""}
                                        onChange={e => handleConfigChange(field.key, e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                    >
                                        <option value="">{field.placeholder}</option>
                                        {field.selectOptions?.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type={field.type}
                                        value={configValues[field.key] || ""}
                                        onChange={e => handleConfigChange(field.key, e.target.value)}
                                        placeholder={field.placeholder}
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                )}
                                {field.example && <p className="text-xs text-gray-500 mt-1">{field.example}</p>}
                            </div>
                        ))}
                        <FormActions isSaving={isSaving} onSave={handleSave} onCancel={() => setIsEditing(false)} />
                    </div>
                ) : isConfigured && config.requiresConfig ? (
                    <ConfigDisplay onEdit={() => setIsEditing(true)}>
                        {config.configFields?.map(field => {
                            const raw = method.config?.[field.key as keyof typeof method.config];
                            let display: string;
                            if (field.type === "password") display = "••••••••";
                            else if (field.key === "extraCharges" || field.key === "amount") display = `₹${raw ?? 0}`;
                            else if (field.type === "select" && field.selectOptions) display = field.selectOptions.find(o => o.value === raw)?.label || String(raw || "Not configured");
                            else display = String(raw || "Not configured");
                            return <p key={field.key} className="text-sm text-blue-700">{field.label}: {display}</p>;
                        })}
                    </ConfigDisplay>
                ) : null}

                {config.infoMessage && (
                    <div className={`${config.infoMessage.bgColor} border ${config.infoMessage.borderColor} rounded-lg p-4 mt-4`}>
                        <div className="flex items-start gap-3">
                            <AlertCircle className={`h-5 w-5 ${config.infoMessage.iconColor} mt-0.5 flex-shrink-0`} />
                            <div>
                                <h4 className={`text-sm font-medium ${config.infoMessage.textColor.replace("700", "800")} mb-1`}>{config.infoMessage.title}</h4>
                                <p className={`text-sm ${config.infoMessage.textColor}`}>{config.infoMessage.message}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Custom cards (Razorpay, Partial COD) ───────────────────────────────────

const GATEWAY_LABELS: Record<string, string> = {
    RAZORPAY: "Razorpay",
    PHONEPE: "PhonePe",
    CASHFREE: "Cashfree",
    MANUAL_UPI: "Manual UPI",
};

const PartialCodCard: React.FC<{ method: PaymentMethod; onToggle: () => void }> = ({ method, onToggle }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [advanceValue, setAdvanceValue] = useState("");
    const [advanceType, setAdvanceType] = useState<"fixed" | "percentage">("fixed");
    const [advanceGateway, setAdvanceGateway] = useState("");
    const [charges, setCharges] = useState<PartialCodCharge[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const { refetch } = usePaymentMethods();
    const toast = useToastStore();

    React.useEffect(() => {
        if (method.config) {
            setAdvanceValue(String(method.config.value ?? ""));
            setAdvanceType(method.config.type ?? "fixed");
            setAdvanceGateway(method.config.advanceGateway ?? "");
            setCharges(method.config.charges ?? []);
        }
    }, [method.config]);

    const isConfigured = !!(method.config?.type && method.config?.advanceGateway);

    const handleSave = async () => {
        if (!advanceValue || !advanceType || !advanceGateway) {
            toast.showToast("Amount, amount type, and gateway are required", "error");
            return;
        }
        if (charges.some(c => !c.label.trim())) {
            toast.showToast("All charge labels are required", "error");
            return;
        }
        setIsSaving(true);
        try {
            await PaymentService.updatePartialCodConfig({ value: Number(advanceValue), type: advanceType, advanceGateway, charges });
            await refetch();
            setIsEditing(false);
            toast.showToast("Partial COD configuration updated successfully", "success");
        } catch {
            toast.showToast("Failed to update Partial COD configuration", "error");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <ConfigCard
            icon={<SplitSquareHorizontal className="h-5 w-5" />}
            iconBgColor="bg-teal-100"
            iconColor="text-teal-600"
            title="Partial COD"
            description="Collect a fixed or percentage-based advance online and the remaining amount on delivery"
            isConfigured={isConfigured}
            isEditing={isEditing}
            onEdit={() => setIsEditing(true)}
            method={method}
            onToggle={onToggle}
            isSaving={isSaving}
        >
            {isEditing ? (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Advance Amount</label>
                        <div className="flex gap-2">
                            <input
                                type="number"
                                value={advanceValue}
                                onChange={e => setAdvanceValue(e.target.value)}
                                placeholder="e.g. 100"
                                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <select
                                value={advanceType}
                                onChange={e => setAdvanceType(e.target.value as "fixed" | "percentage")}
                                className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                            >
                                <option value="fixed">Fixed (₹)</option>
                                <option value="percentage">Percentage (%)</option>
                            </select>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            {advanceType === "fixed" ? "Customer pays this fixed amount online" : "Customer pays this % of cart total online"}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Advance Payment Gateway</label>
                        <select
                            value={advanceGateway}
                            onChange={e => setAdvanceGateway(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                        >
                            <option value="">Select gateway</option>
                            <option value="RAZORPAY">Razorpay</option>
                            <option value="PHONEPE">PhonePe</option>
                            <option value="CASHFREE">Cashfree</option>
                            <option value="MANUAL_UPI">Manual UPI</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Extra Charges</label>
                        <ChargesEditor charges={charges} onChange={setCharges} />
                    </div>
                    <FormActions isSaving={isSaving} onSave={handleSave} onCancel={() => setIsEditing(false)} />
                </div>
            ) : isConfigured ? (
                <ConfigDisplay onEdit={() => setIsEditing(true)}>
                    <p className="text-sm text-blue-700">
                        Advance Amount: {method.config?.type === "percentage" ? `${method.config.value}%` : `₹${method.config?.value}`}
                    </p>
                    <p className="text-sm text-blue-700">
                        Gateway: {GATEWAY_LABELS[method.config?.advanceGateway ?? ""] ?? method.config?.advanceGateway}
                    </p>
                    <ChargesList charges={method.config?.charges ?? []} title="Extra Charges" />
                </ConfigDisplay>
            ) : null}

            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mt-4">
                <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <div>
                        <h4 className="text-sm font-medium text-teal-800 mb-1">How it works</h4>
                        <p className="text-sm text-teal-700">
                            Customers pay an advance amount online at checkout. The remaining balance is collected in cash at delivery. Make sure the selected gateway is already configured.
                        </p>
                    </div>
                </div>
            </div>
        </ConfigCard>
    );
};

const PhonepeCard: React.FC<{ method: PaymentMethod; onToggle: () => void }> = ({ method, onToggle }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [clientId, setClientId] = useState("");
    const [clientSecret, setClientSecret] = useState("");
    const [charges, setCharges] = useState<PartialCodCharge[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const { refetch } = usePaymentMethods();
    const toast = useToastStore();

    React.useEffect(() => {
        if (method.config) {
            setClientId(method.config.clientId ?? "");
            setClientSecret(method.config.clientSecret ?? "");
            setCharges(method.config.charges ?? []);
        }
    }, [method.config]);

    const isConfigured = !!(method.config?.clientId && method.config?.clientSecret);

    const handleSave = async () => {
        if (!clientId || !clientSecret) {
            toast.showToast("Client ID and Client Secret are required", "error");
            return;
        }
        if (charges.some(c => !c.label.trim())) {
            toast.showToast("All charge labels are required", "error");
            return;
        }
        setIsSaving(true);
        try {
            await PaymentService.updatePhonePeConfig(clientId, clientSecret, charges);
            await refetch();
            setIsEditing(false);
            toast.showToast("PhonePe configuration updated successfully", "success");
        } catch {
            toast.showToast("Failed to update PhonePe configuration", "error");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <ConfigCard
            icon={<Smartphone className="h-5 w-5" />}
            iconBgColor="bg-purple-100"
            iconColor="text-purple-600"
            title="PhonePe"
            description="Accept payments through PhonePe UPI, cards, and other digital payment methods"
            isConfigured={isConfigured}
            isEditing={isEditing}
            onEdit={() => setIsEditing(true)}
            method={method}
            onToggle={onToggle}
            isSaving={isSaving}
        >
            {isEditing ? (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
                        <input
                            type="text"
                            value={clientId}
                            onChange={e => setClientId(e.target.value)}
                            placeholder="Enter PhonePe Client ID"
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
                        <input
                            type="password"
                            value={clientSecret}
                            onChange={e => setClientSecret(e.target.value)}
                            placeholder="Enter PhonePe Client Secret"
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Gateway Charges</label>
                        <ChargesEditor
                            charges={charges}
                            onChange={setCharges}
                            labelPlaceholder="e.g. Processing Fee"
                            valuePlaceholder="e.g. 2"
                        />
                    </div>
                    <FormActions isSaving={isSaving} onSave={handleSave} onCancel={() => setIsEditing(false)} />
                </div>
            ) : isConfigured ? (
                <ConfigDisplay onEdit={() => setIsEditing(true)}>
                    <p className="text-sm text-blue-700">Client ID: {method.config?.clientId}</p>
                    <p className="text-sm text-blue-700">Client Secret: ••••••••</p>
                    <ChargesList charges={method.config?.charges ?? []} title="Gateway Charges" />
                </ConfigDisplay>
            ) : null}
        </ConfigCard>
    );
};

const CashfreeCard: React.FC<{ method: PaymentMethod; onToggle: () => void }> = ({ method, onToggle }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [clientId, setClientId] = useState("");
    const [clientSecret, setClientSecret] = useState("");
    const [charges, setCharges] = useState<PartialCodCharge[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const { refetch } = usePaymentMethods();
    const toast = useToastStore();

    React.useEffect(() => {
        if (method.config) {
            setClientId(method.config.clientId ?? "");
            setClientSecret(method.config.clientSecret ?? "");
            setCharges(method.config.charges ?? []);
        }
    }, [method.config]);

    const isConfigured = !!(method.config?.clientId && method.config?.clientSecret);

    const handleSave = async () => {
        if (!clientId || !clientSecret) {
            toast.showToast("App ID and Secret Key are required", "error");
            return;
        }
        if (charges.some(c => !c.label.trim())) {
            toast.showToast("All charge labels are required", "error");
            return;
        }
        setIsSaving(true);
        try {
            await PaymentService.updateCashfreeConfig(clientId, clientSecret, charges);
            await refetch();
            setIsEditing(false);
            toast.showToast("Cashfree configuration updated successfully", "success");
        } catch {
            toast.showToast("Failed to update Cashfree configuration", "error");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <ConfigCard
            icon={<Wallet className="h-5 w-5" />}
            iconBgColor="bg-indigo-100"
            iconColor="text-indigo-600"
            title="Cashfree"
            description="Accept payments through Cashfree UPI, cards, netbanking, and wallets"
            isConfigured={isConfigured}
            isEditing={isEditing}
            onEdit={() => setIsEditing(true)}
            method={method}
            onToggle={onToggle}
            isSaving={isSaving}
        >
            {isEditing ? (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">App ID</label>
                        <input
                            type="text"
                            value={clientId}
                            onChange={e => setClientId(e.target.value)}
                            placeholder="Enter Cashfree App ID"
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Secret Key</label>
                        <input
                            type="password"
                            value={clientSecret}
                            onChange={e => setClientSecret(e.target.value)}
                            placeholder="Enter Cashfree Secret Key"
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Gateway Charges</label>
                        <ChargesEditor
                            charges={charges}
                            onChange={setCharges}
                            labelPlaceholder="e.g. Processing Fee"
                            valuePlaceholder="e.g. 2"
                        />
                    </div>
                    <FormActions isSaving={isSaving} onSave={handleSave} onCancel={() => setIsEditing(false)} />
                </div>
            ) : isConfigured ? (
                <ConfigDisplay onEdit={() => setIsEditing(true)}>
                    <p className="text-sm text-blue-700">App ID: {method.config?.clientId}</p>
                    <p className="text-sm text-blue-700">Secret Key: ••••••••</p>
                    <ChargesList charges={method.config?.charges ?? []} title="Gateway Charges" />
                </ConfigDisplay>
            ) : null}
        </ConfigCard>
    );
};

const RazorpayCard: React.FC<{ method: PaymentMethod; onToggle: () => void }> = ({ method, onToggle }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [keyId, setKeyId] = useState("");
    const [secretKey, setSecretKey] = useState("");
    const [charges, setCharges] = useState<PartialCodCharge[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const { refetch } = usePaymentMethods();
    const toast = useToastStore();

    React.useEffect(() => {
        if (method.config) {
            setKeyId(method.config.keyId ?? "");
            setSecretKey(method.config.secretKey ?? "");
            setCharges(method.config.charges ?? []);
        }
    }, [method.config]);

    const isConfigured = !!(method.config?.keyId && method.config?.secretKey);

    const handleSave = async () => {
        if (!keyId || !secretKey) {
            toast.showToast("Key ID and Secret Key are required", "error");
            return;
        }
        if (charges.some(c => !c.label.trim())) {
            toast.showToast("All charge labels are required", "error");
            return;
        }
        setIsSaving(true);
        try {
            await PaymentService.updateRazorpayConfig(keyId, secretKey, charges);
            await refetch();
            setIsEditing(false);
            toast.showToast("Razorpay configuration updated successfully", "success");
        } catch {
            toast.showToast("Failed to update Razorpay configuration", "error");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <ConfigCard
            icon={<CreditCard className="h-5 w-5" />}
            iconBgColor="bg-green-100"
            iconColor="text-green-600"
            title="Razorpay"
            description="Accept credit/debit cards and other digital payment methods"
            isConfigured={isConfigured}
            isEditing={isEditing}
            onEdit={() => setIsEditing(true)}
            method={method}
            onToggle={onToggle}
            isSaving={isSaving}
        >
            {isEditing ? (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Key ID</label>
                        <input
                            type="text"
                            value={keyId}
                            onChange={e => setKeyId(e.target.value)}
                            placeholder="Enter Razorpay Key ID"
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Secret Key</label>
                        <input
                            type="password"
                            value={secretKey}
                            onChange={e => setSecretKey(e.target.value)}
                            placeholder="Enter Razorpay Secret Key"
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Gateway Charges</label>
                        <ChargesEditor
                            charges={charges}
                            onChange={setCharges}
                            labelPlaceholder="e.g. Processing Fee"
                            valuePlaceholder="e.g. 2.36"
                        />
                    </div>
                    <FormActions isSaving={isSaving} onSave={handleSave} onCancel={() => setIsEditing(false)} />
                </div>
            ) : isConfigured ? (
                <ConfigDisplay onEdit={() => setIsEditing(true)}>
                    <p className="text-sm text-blue-700">Key ID: {method.config?.keyId}</p>
                    <p className="text-sm text-blue-700">Secret Key: ••••••••</p>
                    <ChargesList charges={method.config?.charges ?? []} title="Gateway Charges" />
                </ConfigDisplay>
            ) : null}

            {!isConfigured && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                            <h4 className="text-sm font-medium text-green-800 mb-1">Don't have a Razorpay account?</h4>
                            <p className="text-sm text-green-700 mb-3">
                                Create a free Razorpay account to start accepting cards, UPI, netbanking and wallets. Once your account is approved, come back here and add your Key ID and Secret Key.
                            </p>
                            <a
                                href="https://rzp.io/rzp/cb5GyYYm"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                            >
                                Create Razorpay account
                                <ExternalLink className="h-4 w-4" />
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </ConfigCard>
    );
};

// Global charges (e.g. GST) applied to every order, irrespective of payment method
const OrderChargesCard: React.FC = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [charges, setCharges] = useState<PartialCodCharge[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const { data, refetch } = useQuery({
        queryKey: ["orderCharges"],
        queryFn: () => PaymentService.getOrderCharges(),
    });
    const toast = useToastStore();
    const savedCharges = data?.charges ?? [];

    React.useEffect(() => {
        setCharges(savedCharges);
    }, [data]);

    const handleSave = async () => {
        if (charges.some(c => !c.label.trim())) {
            toast.showToast("All charge labels are required", "error");
            return;
        }
        setIsSaving(true);
        try {
            await PaymentService.updateOrderCharges(charges);
            await refetch();
            setIsEditing(false);
            toast.showToast("Order charges updated successfully", "success");
        } catch {
            toast.showToast("Failed to update order charges", "error");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
            <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-amber-100 flex-shrink-0 flex items-center justify-center">
                            <Receipt className="h-5 w-5 text-amber-600" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900">Order Charges</h3>
                    </div>
                    {!isEditing && savedCharges.length === 0 && (
                        <button onClick={() => setIsEditing(true)} className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm font-medium">
                            Add
                        </button>
                    )}
                </div>
                <p className="text-sm text-gray-600 mb-4">
                    Charges like GST applied to every order, whichever payment method the customer picks. Percentage charges are calculated on the order value (after discounts, including delivery), not on other charges.
                </p>
                {isEditing ? (
                    <div className="space-y-4">
                        <ChargesEditor charges={charges} onChange={setCharges} labelPlaceholder="e.g. GST" valuePlaceholder="e.g. 3" />
                        <FormActions isSaving={isSaving} onSave={handleSave} onCancel={() => { setCharges(savedCharges); setIsEditing(false); }} />
                    </div>
                ) : savedCharges.length > 0 ? (
                    <ConfigDisplay onEdit={() => setIsEditing(true)}>
                        <ChargesList charges={savedCharges} title="Applied to all orders" />
                    </ConfigDisplay>
                ) : null}
            </div>
        </div>
    );
};

// ─── Page ────────────────────────────────────────────────────────────────────

const defaultPaymentMethods: PaymentMethod[] = [
    { _id: "razorpay-default", entityId: 1, name: "Razorpay", enabled: false, type: "RAZORPAY", config: {} },
    { _id: "phonepe-default", entityId: 1, name: "PhonePe", enabled: false, type: "PHONEPE", config: {} },
    { _id: "cashfree-default", entityId: 1, name: "Cashfree", enabled: false, type: "CASHFREE", config: {} },
    { _id: "cod-default", entityId: 1, name: "Cash on Delivery", enabled: false, type: "COD", config: {} },
    { _id: "manual-upi-default", entityId: 1, name: "Manual UPI Payment", enabled: false, type: "MANUAL_UPI", config: {} },
    { _id: "partial-cod-default", entityId: 1, name: "Partial COD", enabled: false, type: "PARTIAL_COD", config: {} },
];

const PaymentMethods = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalConfig, setModalConfig] = useState({ title: "", message: "", onConfirm: () => { } });
    const { data: paymentMethods, isLoading, isError, refetch } = usePaymentMethods();
    const toast = useToastStore();

    const handleToggle = async (methodId: string, isEnabled: boolean) => {
        setModalConfig({
            title: "Confirm Toggle",
            message: `Are you sure you want to ${isEnabled ? "disable" : "enable"} this payment method?`,
            onConfirm: async () => {
                try {
                    await PaymentService.enableDisablePaymentMethod(methodId);
                    refetch();
                    toast.showToast(`Payment method ${isEnabled ? "disabled" : "enabled"} successfully`, "success");
                } catch (error) {
                    console.error("Failed to toggle payment method:", error);
                    toast.showToast("Failed to update payment method status. Please try again.", "error");
                } finally {
                    setModalOpen(false);
                }
            },
        });
        setModalOpen(true);
    };

    if (isLoading) return <div className="flex justify-center items-center h-screen">Loading...</div>;
    if (isError) return <div className="flex justify-center items-center h-screen text-red-600">Error loading payment methods</div>;

    const allMethods = defaultPaymentMethods.map(d =>
        paymentMethods?.find((m: PaymentMethod) => m.type === d.type) || d
    );

    return (
        <div className="bg-gray-50 min-h-screen">
            <Modal
                isOpen={modalOpen}
                title={modalConfig.title}
                message={modalConfig.message}
                onConfirm={modalConfig.onConfirm}
                onCancel={() => setModalOpen(false)}
            />
            <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
                {allMethods.map(method => {
                    const toggle = () => handleToggle(method._id, method.enabled);
                    if (method.type === "PARTIAL_COD") return <PartialCodCard key={method._id} method={method} onToggle={toggle} />;
                    if (method.type === "RAZORPAY") return <RazorpayCard key={method._id} method={method} onToggle={toggle} />;
                    if (method.type === "PHONEPE") return <PhonepeCard key={method._id} method={method} onToggle={toggle} />;
                    if (method.type === "CASHFREE") return <CashfreeCard key={method._id} method={method} onToggle={toggle} />;
                    return <PaymentMethodCard key={method._id} method={method} onToggle={toggle} />;
                })}
                <OrderChargesCard />
            </div>
        </div>
    );
};

export default PaymentMethods;
